import { useEffect, useRef, useState } from 'react';

import { apiUrl } from '../../lib/apiBase';

export default function AddressAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder = 'Enter an address, city, or state',
  className = '',
  error,
  ...rest
}) {
  const [predictions, setPredictions] = useState([]);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (!value || value.trim().length < 2) {
      setPredictions([]);
      setOpen(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(
          apiUrl(`/api/geography/address-autocomplete?input=${encodeURIComponent(value.trim())}`)
        );
        const data = await res.json();
        if (data.ok && data.predictions?.length) {
          setPredictions(data.predictions);
          setOpen(true);
          setActiveIndex(-1);
        } else {
          setPredictions([]);
          setOpen(false);
        }
      } catch {
        setPredictions([]);
        setOpen(false);
      }
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [value]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function handleSelect(description) {
    onSelect?.(description);
    onChange?.(description);
    setOpen(false);
    setPredictions([]);
  }

  function handleKeyDown(e) {
    if (!open || !predictions.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, predictions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, -1));
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      handleSelect(predictions[activeIndex].description);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  const baseClass = [
    'w-full px-4 py-2.5 text-base border rounded-lg transition-colors duration-200',
    'focus:outline-none focus:ring-2 focus:ring-offset-1',
    error
      ? 'border-error-500 focus:border-error-500 focus:ring-error-500'
      : 'border-neutral-300 focus:border-primary-500 focus:ring-primary-500',
    className,
  ].join(' ');

  return (
    <div ref={containerRef} className="relative">
      <input
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange?.(e.target.value)}
        onKeyDown={handleKeyDown}
        autoComplete="off"
        aria-autocomplete="list"
        aria-expanded={open}
        className={baseClass}
        {...rest}
      />
      {error && <p className="mt-1 text-sm text-error-600">{error}</p>}
      {open && predictions.length > 0 && (
        <ul
          role="listbox"
          className="absolute z-50 mt-1 w-full rounded-lg border border-neutral-200 bg-white shadow-lg overflow-hidden"
        >
          {predictions.map((p, i) => (
            <li
              key={p.place_id}
              role="option"
              aria-selected={i === activeIndex}
              onMouseDown={() => handleSelect(p.description)}
              onMouseEnter={() => setActiveIndex(i)}
              className={[
                'px-4 py-2.5 cursor-pointer text-sm',
                i === activeIndex ? 'bg-primary-50 text-primary-700' : 'hover:bg-neutral-50',
              ].join(' ')}
            >
              {p.main_text && <span className="font-medium">{p.main_text}</span>}
              {p.secondary_text && (
                <span className="text-neutral-500 ml-1">{p.secondary_text}</span>
              )}
              {!p.main_text && p.description}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
